import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    Button, Input, Select, InputNumber, Modal, Form, Empty, message, Drawer,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { requestWorkouts, createWorkout, requestPlans, assertDaemonCompatible, checkDaemonCompatibility } from './store/store'
import { setSelectedWorkout, resetCreateStatus } from './store/workouts'
import { planRangeKey } from './store/plans'
import { getLocalStorageValue } from './store/localstorage'
import { showDaemonBlockModal } from './DaemonBlockUi'
import {
    ZONES,
    filterWorkouts, sortWorkouts,
    formatDuration,
    zonePercents, formatCardMetric,
    computePlannedList, formatPlannedCell,
    plannedZoneKey, sortPlannedList,
} from './workoutLogic'
import WorkoutProfileThumbnail from './WorkoutProfileThumbnail'
import { useWorkoutFavorites } from './useWorkoutFavorites'
import { useLocalToday } from './useLocalToday'
import { expandDesignerRows, FTP_KEY } from './playerLogic'
import './css/workouts.css'

const TABS = [
    { key: 'all', label: 'Все' },
    { key: 'favorites', label: 'Избранные' },
    { key: 'mine', label: 'Мои' },
    { key: 'planned', label: 'Запланировано' },
]

const ZONE_OPTIONS = ZONES.map(([value, label]) => ({ value, label }))

const DURATION_OPTIONS = [
    { value: 'lt30', label: 'до 30 мин' },
    { value: '30to60', label: '30–60 мин' },
    { value: 'gt60', label: 'более 60 мин' },
]

const SORT_OPTIONS = [
    { value: 'default', label: 'По умолчанию' },
    { value: 'duration', label: 'По длительности' },
    { value: 'name', label: 'По названию' },
    { value: 'author', label: 'По автору' },
    { value: 'zone', label: 'По времени в зоне' },
]

function readPlannedUrl() {
    if (typeof window === 'undefined') return null
    const params = new URLSearchParams(window.location.search)
    if (params.get('category') !== 'planned') return null
    return {
        date: params.get('date') || null,
    }
}

function WorkoutZoneDistribution({ workout }) {
    const rootRef = useRef(null)
    const [shortLabels, setShortLabels] = useState(false)

    useEffect(() => {
        const el = rootRef.current
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const narrowViewport = () => typeof window !== 'undefined' && window.innerWidth <= 620
        const update = () => setShortLabels(narrowViewport() || el.clientWidth / 7 < 56)
        const observer = new ResizeObserver(update)
        observer.observe(el)
        update()
        return () => observer.disconnect()
    }, [])

    const zones = zonePercents(workout)
    const hasData = zones.some(z => z.pct > 0)
    const maxPct = hasData ? Math.max(...zones.map(z => z.pct)) : 1

    return (
        <div ref={rootRef}
            className={hasData ? 'workout-card__zones' : 'workout-card__zones is-empty'}
            role="img" aria-label="Распределение по зонам мощности">
            <p className="workout-card__zones-title">Распределение по мощности</p>
            <div className="workout-card__zones-bars">
                {zones.map((z, i) => {
                    const height = hasData && z.pct > 0
                        ? `${Math.max(6, Math.round(z.pct / maxPct * 100))}%`
                        : '0%'
                    return (
                        <div
                            className="workout-card__zone"
                            key={z.key}
                            data-tooltip={`${z.label} · ${hasData ? `${z.pct.toFixed(1)}%` : '0%'} · ${formatDuration(z.seconds)}`}
                        >
                            <span className="workout-card__zone-value">{hasData ? `${Math.round(z.pct)}%` : '0%'}</span>
                            <span className="workout-card__zone-track">
                                <span className="workout-card__zone-bar" style={{ height }} />
                            </span>
                            <span className="workout-card__zone-label">{shortLabels ? `Z${i + 1}` : z.label}</span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

function PlannedCard({ row, favoriteIds, toggleFavorite, onStart, onSelect }) {
    const workout = row.catalogue || { id: row.workout_id }
    const duration = row.workout && row.workout.duration_s
    const tss = row.workout && row.workout.tss
    const if_ = row.workout && row.workout.if_
    return (
        <article className="workout-card" data-planned-id={row.id}>
            <button className="workout-card__select" type="button"
                onClick={() => onSelect(workout)}>
                {row.points
                    ? <WorkoutProfileThumbnail points={row.points} />
                    : <div className="workout-profile workout-profile--placeholder" role="img" aria-label="Профиль мощности недоступен" />}
                <div className="workout-card__body">
                    <h2>{row.workout.name || 'Без названия'}</h2>
                    <p className="workout-card__author">{row.author || 'Автор не указан'}</p>
                    <dl className="workout-card__metrics">
                        <div><dt>Дата</dt><dd>{formatPlannedCell(row.date)}</dd></div>
                        <div><dt>Время</dt><dd>{duration != null ? formatDuration(duration) : '—'}</dd></div>
                        <div><dt>Нагрузка</dt><dd>{formatCardMetric(tss, 0)}</dd></div>
                        <div><dt>Интенсивность</dt><dd>{formatCardMetric(if_, 2)}</dd></div>
                    </dl>
                </div>
            </button>
            <WorkoutZoneDistribution workout={row} />
            <div className="workout-card__actions">
                <Button type="text"
                    aria-pressed={favoriteIds.includes(workout.id)}
                    aria-label={favoriteIds.includes(workout.id) ? 'Удалить из избранного' : 'Добавить в избранное'}
                    onClick={() => toggleFavorite(workout.id)}>
                    {favoriteIds.includes(workout.id) ? '★' : '☆'}
                </Button>
                <Button type="primary" onClick={() => onStart(row)}>Начать</Button>
            </div>
        </article>
    )
}

function DesignerModal({ open, onClose, onCreated }) {
    const [form] = Form.useForm()
    const dispatch = useDispatch()
    const createStatus = useSelector(state => state.workouts.createStatus)

    useEffect(() => {
        if (open) {
            dispatch(resetCreateStatus())
            form.setFieldsValue({
                author: '',
                name: '',
                rows: [{ target_pct: 100, time_s: 60, repeats: 1 }],
            })
        }
    }, [open, form, dispatch])

    const handleOk = async () => {
        const values = await form.validateFields()
        const points = expandDesignerRows(values.rows)
        try {
            await createWorkout({
                author: values.author || '—',
                name: values.name,
                points,
                origin: {
                    source: 'web-designer',
                    key: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
                },
            })
            message.success('Тренировка создана')
            onCreated()
            onClose()
        } catch (e) {
            const msg = (e && e.code === 'Duplicate')
                ? 'Такая тренировка уже существует'
                : ((e && e.message) || 'Не удалось создать тренировку')
            message.error(msg)
        }
    }

    return (
        <Modal
            title="Создать тренировку"
            visible={open}
            onOk={handleOk}
            onCancel={onClose}
            okText="Создать"
            cancelText="Отмена"
            confirmLoading={createStatus === 'loading'}
            destroyOnClose
        >
            <Form form={form} layout="vertical" name="designer">
                <Form.Item name="author" label="Автор">
                    <Input placeholder="Автор" />
                </Form.Item>
                <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Укажите название' }]}>
                    <Input placeholder="Название тренировки" />
                </Form.Item>
                <Form.List name="rows">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <div className="designer-row" key={field.key}>
                                    <Form.Item name={[field.name, 'target_pct']} rules={[{ required: true, message: 'Цель' }]}
                                        label={index === 0 ? 'Цель, % FTP' : ''}>
                                        <InputNumber min={0} max={130} style={{ width: '100%' }} placeholder="%" />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'time_s']} rules={[{ required: true, message: 'Время' }]}
                                        label={index === 0 ? 'Время, с' : ''}>
                                        <InputNumber min={1} style={{ width: '100%' }} placeholder="с" />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'repeats']} rules={[{ required: true, message: 'Повторы' }]}
                                        label={index === 0 ? 'Повторы' : ''}>
                                        <InputNumber min={1} style={{ width: '100%' }} placeholder="раз" />
                                    </Form.Item>
                                    <Form.Item label=" " colon={false} className="designer-row__remove">
                                        <Button icon={<DeleteOutlined />} onClick={() => remove(field.name)} aria-label="Удалить интервал" />
                                    </Form.Item>
                                </div>
                            ))}
                            <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ target_pct: 100, time_s: 60, repeats: 1 })}>
                                Добавить интервал
                            </Button>
                        </>
                    )}
                </Form.List>
            </Form>
        </Modal>
    )
}

function ViewWorkouts({ onStartWorkout, onNavigate }) {
    const dispatch = useDispatch()
    const today = useLocalToday()
    const workouts = useSelector(state => state.workouts.workouts)
    const loading = useSelector(state => state.workouts.loading)

    const plannedUrl = useMemo(readPlannedUrl, [])
    const [searchText, setSearchText] = useState('')
    const [activeTab, setActiveTab] = useState(() => (plannedUrl ? 'planned' : 'all'))
    const [durationFilter, setDurationFilter] = useState(undefined)
    const [zoneFilter, setZoneFilter] = useState([])
    const [zoneMatch, setZoneMatch] = useState('all')
    const [minimumShare, setMinimumShare] = useState(20)
    const [sortKey, setSortKey] = useState('default')
    const { favoriteIds, toggleFavorite } = useWorkoutFavorites()
    const [designerOpen, setDesignerOpen] = useState(false)
    const [drawerOpen, setDrawerOpen] = useState(false)

    const [plannedDate, setPlannedDate] = useState(() => (plannedUrl && plannedUrl.date) || today)

    const isPlannedTab = activeTab === 'planned'
    const plannedKey = planRangeKey(plannedDate, plannedDate)
    const plannedRange = useSelector(state => state.plans.ranges[plannedKey])

    useEffect(() => {
        requestWorkouts()
        checkDaemonCompatibility()
    }, [])

    useEffect(() => {
        if (!isPlannedTab) return
        requestPlans({ dateFrom: plannedDate, dateTo: plannedDate })
    }, [isPlannedTab, plannedDate])

    // URL search params are the sole persistence for the planned category.
    useEffect(() => {
        if (!isPlannedTab || typeof window === 'undefined') return
        const params = new URLSearchParams()
        params.set('category', 'planned')
        params.set('date', plannedDate)
        const qs = params.toString()
        const currentQs = window.location.search.startsWith('?') ? window.location.search.slice(1) : ''
        if (currentQs !== qs) {
            window.history.replaceState(null, '', `${window.location.pathname}?${qs}`)
        }
    }, [isPlannedTab, plannedDate])

    const visibleWorkouts = useMemo(() => {
        const filtered = filterWorkouts(workouts, {
            searchText,
            tab: activeTab,
            durationFilter,
            zoneFilter,
            zoneMatch,
            minimumShare,
            favoriteIds,
        })
        const zoneKey = zoneFilter && zoneFilter.length ? zoneFilter[0] : 'threshold'
        return sortWorkouts(filtered, { sortKey, zoneKey })
    }, [workouts, searchText, activeTab, durationFilter, zoneFilter, zoneMatch, minimumShare, sortKey, favoriteIds])

    const workoutsById = useMemo(() => {
        const map = {}
        for (const w of workouts || []) map[w.id] = w
        return map
    }, [workouts])

    const plannedRows = useMemo(() => {
        const plans = plannedRange ? plannedRange.rows : []
        return sortPlannedList(computePlannedList(plans, plannedDate)).map(p => {
            const catalogue = workoutsById[p.workout_id]
            const row = { ...p, catalogue }
            row.zoneKey = plannedZoneKey(catalogue || { properties: [] })
            row.points = catalogue ? catalogue.points : undefined
            row.properties = catalogue ? catalogue.properties : undefined
            row.author = (p.workout && p.workout.author) || (catalogue && catalogue.author) || ''
            return row
        })
    }, [plannedRange, plannedDate, workoutsById])

    const plannedLoading = Boolean(plannedRange && plannedRange.status === 'loading')

    const openPlayer = async workout => {
        const verdict = await assertDaemonCompatible()
        if (!verdict.ok) {
            showDaemonBlockModal(verdict)
            return
        }
        const ftp = getLocalStorageValue(FTP_KEY, 200) || 200
        onStartWorkout(workout, ftp)
    }

    const startPlanned = async row => {
        const verdict = await assertDaemonCompatible()
        if (!verdict.ok) {
            showDaemonBlockModal(verdict)
            return
        }
        const ftp = getLocalStorageValue(FTP_KEY, 200) || 200
        const workout = row.catalogue || {
            id: row.workout_id,
            name: row.workout.name,
            author: row.author,
            points: [],
            properties: [],
        }
        onStartWorkout(workout, ftp)
    }

    const selectWorkout = workout => {
        dispatch(setSelectedWorkout(workout))
    }

    const clearFilters = () => {
        setSearchText('')
        setDurationFilter(undefined)
        setZoneFilter([])
        setZoneMatch('all')
        setMinimumShare(20)
        setSortKey('default')
    }

    const hasFilters = searchText || durationFilter || (zoneFilter && zoneFilter.length) || sortKey !== 'default'

    const catalogueToolbar = (
        <>
            <Input.Search
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Поиск по названию или автору"
                allowClear
            />
            <Select value={durationFilter} onChange={setDurationFilter} allowClear
                placeholder="Длительность" options={DURATION_OPTIONS} />
            <Select mode="multiple" value={zoneFilter} onChange={setZoneFilter} allowClear
                placeholder="Зоны" options={ZONE_OPTIONS} maxTagCount="responsive" />
            <Select value={zoneMatch} onChange={setZoneMatch}
                options={[{ value: 'all', label: 'Все выбранные зоны' }, { value: 'any', label: 'Любая выбранная зона' }]} />
            <InputNumber min={0} max={100} value={minimumShare} onChange={setMinimumShare}
                addonAfter="%" placeholder="мин. доля" />
            <Select value={sortKey} onChange={setSortKey} options={SORT_OPTIONS} />
            <Select disabled placeholder="Сложность" style={{ width: '100%' }} />
            <Button onClick={clearFilters}>Сбросить</Button>
        </>
    )

    const plannedToolbar = (
        <>
            <Input type="date" className="workouts-planned-date" aria-label="Дата" value={plannedDate} onChange={e => setPlannedDate(e.target.value)} />
            <Button className="workouts-open-planner" onClick={() => onNavigate && onNavigate('planner')}>Открыть планировщик</Button>
        </>
    )

    return (
        <main className="workouts-catalogue">
            <div className="workouts-catalogue__inner">
                <nav className="workouts-tabs" aria-label="Каталог тренировок">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            type="button"
                            className={tab.key === activeTab ? 'is-active' : ''}
                            aria-pressed={tab.key === activeTab}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <header className="workouts-heading">
                    <div>
                        <p className="workouts-eyebrow">ТРЕНИРОВКИ</p>
                        <h1>{isPlannedTab ? 'Запланированные тренировки' : 'Все тренировки'}</h1>
                    </div>
                    <Button type="primary" onClick={() => setDesignerOpen(true)}>Создать тренировку</Button>
                </header>

                <section className={`workouts-toolbar${isPlannedTab ? ' workouts-toolbar--planned' : ''}`} aria-label={isPlannedTab ? 'Фильтры запланированных тренировок' : 'Фильтры тренировок'}>
                    {isPlannedTab ? plannedToolbar : catalogueToolbar}
                    <span className="workouts-toolbar__mobile-filters">
                        <Button onClick={() => setDrawerOpen(true)}>Фильтры</Button>
                    </span>
                </section>

                <div className="workouts-list-header">
                    {isPlannedTab
                        ? <span>Запланировано: {plannedRows.length}</span>
                        : <span>Показано {visibleWorkouts.length} из {(workouts || []).length} тренировок</span>}
                    {loading && <span aria-live="polite">Загрузка...</span>}
                    {!isPlannedTab && hasFilters && <button type="button" className="workouts-clear-filters" onClick={clearFilters}>Сбросить фильтры</button>}
                </div>

                {isPlannedTab ? (
                    <section className="workouts-list" aria-label="Список запланированных тренировок">
                        {plannedRows.map(row => (
                            <PlannedCard
                                key={row.id}
                                row={row}
                                favoriteIds={favoriteIds}
                                toggleFavorite={toggleFavorite}
                                onStart={startPlanned}
                                onSelect={selectWorkout}
                            />
                        ))}
                        {!plannedLoading && plannedRows.length === 0 && <Empty description="На эту дату ничего не запланировано" />}
                    </section>
                ) : (
                    <section className="workouts-list" aria-label="Список тренировок">
                        {(visibleWorkouts || []).map(workout => (
                            <article className="workout-card" key={workout.id}>
                                <button className="workout-card__select" type="button"
                                    onClick={() => selectWorkout(workout)}>
                                    <WorkoutProfileThumbnail points={workout.points} />
                                    <div className="workout-card__body">
                                        <h2>{workout.name || 'Без названия'}</h2>
                                        <p className="workout-card__author">{workout.author || 'Автор не указан'}</p>
                                        <dl className="workout-card__metrics">
                                            <div><dt>Время</dt><dd>{workout.duration_s != null ? formatDuration(workout.duration_s) : '—'}</dd></div>
                                            <div><dt>Нагрузка</dt><dd>{formatCardMetric(workout.tss, 0)}</dd></div>
                                            <div><dt>Интенсивность</dt><dd>{formatCardMetric(workout.if_, 2)}</dd></div>
                                            <div><dt>KJ (CAL)</dt><dd>{formatCardMetric(workout.kj, 0)}</dd></div>
                                        </dl>
                                    </div>
                                </button>
                                <WorkoutZoneDistribution workout={workout} />
                                <div className="workout-card__actions">
                                    <Button type="text"
                                        aria-pressed={favoriteIds.includes(workout.id)}
                                        aria-label={favoriteIds.includes(workout.id) ? 'Удалить из избранного' : 'Добавить в избранное'}
                                        onClick={() => toggleFavorite(workout.id)}>
                                        {favoriteIds.includes(workout.id) ? '★' : '☆'}
                                    </Button>
                                    <Button type="primary" onClick={() => openPlayer(workout)}>Начать</Button>
                                </div>
                            </article>
                        ))}
                        {!loading && (visibleWorkouts || []).length === 0 && <Empty description="Тренировки не найдены" />}
                    </section>
                )}
            </div>

            <Drawer title="Фильтры" placement="right" width={320} visible={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <div className="workouts-drawer-filters">
                    <Input.Search
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="Поиск по названию или автору"
                        allowClear
                    />
                    <Select value={durationFilter} onChange={setDurationFilter} allowClear placeholder="Длительность" options={DURATION_OPTIONS} style={{ width: '100%' }} />
                    <Select mode="multiple" value={zoneFilter} onChange={setZoneFilter} allowClear placeholder="Зоны" options={ZONE_OPTIONS} style={{ width: '100%' }} />
                    <Select value={zoneMatch} onChange={setZoneMatch} options={[{ value: 'all', label: 'Все выбранные зоны' }, { value: 'any', label: 'Любая выбранная зона' }]} style={{ width: '100%' }} />
                    <InputNumber min={0} max={100} value={minimumShare} onChange={setMinimumShare} addonAfter="%" style={{ width: '100%' }} />
                    <Select value={sortKey} onChange={setSortKey} options={SORT_OPTIONS} style={{ width: '100%' }} />
                    <Button block onClick={() => { clearFilters(); setDrawerOpen(false) }}>Сбросить</Button>
                </div>
            </Drawer>

            <DesignerModal open={designerOpen} onClose={() => setDesignerOpen(false)} onCreated={() => requestWorkouts()} />
        </main>
    )
}

export default ViewWorkouts